// SarvaRitam service architecture — CMS-ready content structure.
// Every service follows the same editorial contract: what / why / who / what we do /
// deliverables / frameworks / process / next step.

export const categories = [
  {
    id: "environment",
    name: "Environmental",
    tagline: "Measure, manage and reduce your environmental footprint with defensible data.",
    blurb:
      "From GHG inventories to nature-related risk, environmental advisory built on traceable activity data and recognised methodologies.",
  },
  {
    id: "social",
    name: "Social",
    tagline: "Understand your people, your value chain and your communities.",
    blurb:
      "Social performance is where ESG becomes human: diversity, human rights, community impact and internal capability.",
  },
  {
    id: "governance",
    name: "Governance",
    tagline: "Build the structures that make ESG decisions credible.",
    blurb:
      "Policies, oversight structures and risk processes that turn sustainability intent into accountable management.",
  },
  {
    id: "reporting",
    name: "ESG Reporting & Disclosure",
    tagline: "Report once, report well — and stand behind every number.",
    blurb:
      "BRSR, international standards and investor disclosures prepared with evidence, controls and management review.",
  },
  {
    id: "responsible-investment",
    name: "Responsible Investment",
    tagline: "ESG for capital: integration, diligence and portfolio oversight.",
    blurb:
      "A dedicated practice for investors, funds and financial institutions deploying ESG across the investment process.",
  },
];

const svc = (s) => s;

export const services = [
  // ---------------- ENVIRONMENT ----------------
  svc({
    slug: "ghg-accounting",
    category: "environment",
    name: "GHG Accounting & Carbon Footprinting",
    short: "A credible, auditable inventory of your Scope 1, 2 and 3 emissions.",
    what:
      "GHG accounting quantifies the greenhouse gas emissions your organisation is responsible for, organised into Scope 1 (direct), Scope 2 (purchased energy) and Scope 3 (value chain). A well-built inventory is the foundation for targets, disclosures and reduction plans.",
    why:
      "Almost every ESG disclosure, client questionnaire and net-zero commitment ultimately rests on a defensible carbon baseline. Without a structured inventory, organisations struggle to compare years, evidence reductions or respond credibly to investors and regulators.",
    who: ["Corporates preparing BRSR or sustainability reports", "Organisations with net-zero commitments", "Supply-chain suppliers facing customer data requests", "Investors assessing portfolio emissions"],
    do: [
      "Map your emission sources and organisational boundaries",
      "Build a GHG inventory across Scope 1, 2 and priority Scope 3 categories",
      "Assess and document emission factors and data quality",
      "Align methodology with the GHG Protocol and ISO 14064 logic",
      "Establish repeatable data-collection routines for future years",
    ],
    deliverables: ["Scope 1, 2 and 3 GHG inventory", "Emission factor assessment and data-quality notes", "Carbon footprint report", "Improvement and data-controls roadmap"],
    frameworks: ["GHG Protocol", "ISO 14064", "BRSR", "IFRS S2 (climate-related)"],
    process: ["Boundary and source mapping", "Data collection and validation", "Inventory build and factor assessment", "Review, documentation and handover"],
    next: "Start Your Carbon Baseline",
  }),
  svc({
    slug: "climate-risk",
    category: "environment",
    name: "Climate Risk Assessment",
    short: "Understand physical and transition risk before it understands you.",
    what:
      "Climate risk assessment examines how physical climate hazards (heat, water stress, flooding, cyclones) and the transition to a lower-carbon economy (policy, technology, market shifts) could affect your operations, value chain and strategy.",
    why:
      "Climate exposure is increasingly part of disclosure expectations, lending decisions and board-level risk oversight. Scenario-based assessment replaces vague concern with a structured view of where your organisation is exposed and what could be done about it.",
    who: ["Boards and risk committees", "Infrastructure and real asset owners", "Financial institutions with climate exposure", "Listed companies with disclosure obligations"],
    do: [
      "Identify physical and transition risk exposure across operations and value chain",
      "Run structured scenario analysis aligned with TCFD thinking",
      "Assess climate opportunities alongside risks",
      "Review climate governance and risk integration",
    ],
    deliverables: ["Physical & transition risk assessment", "Scenario analysis", "Climate opportunity assessment", "Climate governance and strategy recommendations"],
    frameworks: ["TCFD", "IFRS S2", "BRSR", "ISSB"],
    process: ["Exposure mapping", "Scenario selection and analysis", "Risk evaluation and prioritisation", "Governance and strategy integration"],
    next: "Assess Your Climate Risk",
  }),
  svc({
    slug: "net-zero",
    category: "environment",
    name: "Net Zero & Decarbonisation",
    short: "From baseline to prioritised, practical reduction roadmap.",
    what:
      "Decarbonisation advisory turns your carbon inventory into a sequenced plan: baseline assessment, reduction opportunities, target setting and an implementation roadmap proportionate to your organisation.",
    why:
      "Commitments without a credible pathway invite scrutiny. A decarbonisation roadmap connects emissions data to capital planning, procurement and operations so reduction becomes a managed programme rather than an aspiration.",
    who: ["Organisations setting or validating targets", "Manufacturers with energy-intensive operations", "Companies responding to customer net-zero requirements", "Boards evaluating transition plans"],
    do: [
      "Establish or refine your emissions baseline",
      "Identify and prioritise emission reduction opportunities",
      "Support target setting and internal business cases",
      "Sequence measures into a phased implementation roadmap",
    ],
    deliverables: ["Baseline assessment", "Reduction opportunity register", "Decarbonisation roadmap", "Target-setting recommendations"],
    frameworks: ["GHG Protocol", "ISO 14064", "IFRS S2", "UN SDGs"],
    process: ["Baseline and hotspot analysis", "Opportunity assessment", "Target and pathway design", "Implementation roadmap"],
    next: "Plan Your Net Zero Pathway",
  }),
  svc({
    slug: "biodiversity",
    category: "environment",
    name: "Biodiversity Assessment",
    short: "Nature-related dependencies, impacts and risks, TNFD-aligned.",
    what:
      "Biodiversity assessment examines how your organisation depends on and impacts ecosystems — land, water, species and habitats — and what that means for risk, compliance and resilience.",
    why:
      "Nature is moving into the mainstream of ESG expectations. Regulators, lenders and customers increasingly ask how organisations understand their relationship with nature; TNFD-aligned thinking provides the emerging structure for the answer.",
    who: ["Land, water and resource-intensive sectors", "Financial institutions assessing nature exposure", "Infrastructure and project developers", "Organisations exploring TNFD-aligned disclosure"],
    do: [
      "Assess biodiversity dependencies and impacts",
      "Identify nature-related risks at site and sector level",
      "Apply TNFD-aligned thinking to structure findings",
      "Identify mitigation and enhancement opportunities",
    ],
    deliverables: ["Biodiversity dependency & impact assessment", "Nature-related risk review", "Site and sector assessment", "Mitigation opportunities"],
    frameworks: ["TNFD", "UN SDGs", "GRI"],
    process: ["Scope and site mapping", "Dependency and impact assessment", "Risk evaluation", "Mitigation and next steps"],
    next: "Understand Nature-Related Risk",
  }),
  svc({
    slug: "water-resources",
    category: "environment",
    name: "Water & Resource Assessment",
    short: "Water footprint, resource efficiency and circularity, assessed together.",
    what:
      "We assess water consumption and risk, resource efficiency and circularity across your operations — how much you use, where you are exposed, and where efficiency measures create both environmental and commercial value.",
    why:
      "Water stress, resource price volatility and waste regulation make resource performance a boardroom issue in many sectors. Structured assessment shows where efficiency pays back and where risk needs managing.",
    who: ["Manufacturing and processing sites", "Facilities and real estate operators", "Organisations in water-stressed regions", "Teams building environmental programmes"],
    do: [
      "Assess water consumption and water risk",
      "Map resource flows and waste streams",
      "Evaluate circularity opportunities",
      "Recommend efficiency and management measures",
    ],
    deliverables: ["Water consumption & risk assessment", "Water footprint", "Resource efficiency review", "Circularity assessment"],
    frameworks: ["GRI", "BRSR", "UN SDGs"],
    process: ["Consumption and risk mapping", "Efficiency and circularity review", "Prioritisation", "Management recommendations"],
    next: "Review Your Resource Footprint",
  }),
  svc({
    slug: "environmental-strategy",
    category: "environment",
    name: "Environmental Strategy",
    short: "Policies, management systems and priorities that hold together.",
    what:
      "Environmental strategy brings your policies, management systems, resource plans and waste strategy into one coherent programme connected to business objectives.",
    why:
      "Fragmented environmental activity — a policy here, a target there — is hard to evidence and harder to improve. A coherent strategy gives management a clear line of sight from intent to measurable outcomes.",
    who: ["Organisations formalising their ESG programme", "Operations and EHS leadership", "Companies preparing for disclosure or client audits"],
    do: [
      "Draft environmental policy and management frameworks",
      "Design environmental management system structures",
      "Prioritise resource efficiency and waste strategy",
      "Connect environmental goals to governance and KPIs",
    ],
    deliverables: ["Environmental policy", "Environmental management system design", "Resource efficiency plan", "Waste strategy & circular economy approach"],
    frameworks: ["GRI", "BRSR", "ISO 14001-aligned thinking"],
    process: ["Current-state review", "Strategy and policy design", "Governance and KPI linkage", "Implementation support"],
    next: "Build Your Environmental Strategy",
  }),

  // ---------------- SOCIAL ----------------
  svc({
    slug: "diversity-inclusion",
    category: "social",
    name: "Diversity & Inclusion Assessment",
    short: "Evidence-based D&I: workforce data, pay equity and practices.",
    what:
      "A structured assessment of workforce diversity, gender representation, pay equity, leadership diversity and inclusion practices — producing a maturity view rather than assumptions.",
    why:
      "D&I is increasingly examined by investors, raters and BRSR disclosures. Beyond disclosure, organisations need to know whether their culture and practices genuinely support inclusion, and where to focus.",
    who: ["HR leadership and DEI teams", "Boards reviewing people metrics", "Listed companies with BRSR social disclosures", "Organisations building inclusion programmes"],
    do: [
      "Assess workforce diversity and representation data",
      "Review pay equity considerations and methodology",
      "Evaluate leadership diversity and inclusion practices",
      "Benchmark D&I maturity and recommend priorities",
    ],
    deliverables: ["Workforce diversity assessment", "Pay equity review", "Inclusion practice evaluation", "D&I maturity assessment"],
    frameworks: ["BRSR", "GRI", "UN SDGs"],
    process: ["Data review and baseline", "Practice and policy evaluation", "Maturity assessment", "Prioritised action plan"],
    next: "Assess Your D&I Maturity",
  }),
  svc({
    slug: "human-rights",
    category: "social",
    name: "Human Rights Assessment",
    short: "Policy, due diligence and grievance mechanisms that hold up.",
    what:
      "We help organisations understand human rights considerations across their operations and supply chains: policy commitments, risk identification, due diligence, labour practices and grievance mechanisms.",
    why:
      "Supply-chain scrutiny, disclosure expectations and customer requirements are raising the bar on human rights. Organisations need documented, proportionate due diligence — not just statements of principle.",
    who: ["Companies with extended supply chains", "Consumer and retail businesses", "Exporters facing buyer requirements", "Boards setting ethical commitments"],
    do: [
      "Draft or review human rights policy commitments",
      "Identify salient human rights risks",
      "Design proportionate due diligence processes",
      "Review labour practices and grievance mechanisms",
    ],
    deliverables: ["Human rights policy", "Risk identification & due diligence framework", "Labour practice review", "Grievance mechanism recommendations"],
    frameworks: ["UN SDGs", "GRI", "BRSR"],
    process: ["Policy and scope definition", "Risk identification", "Due diligence design", "Remediation and review"],
    next: "Review Your Human Rights Approach",
  }),
  svc({
    slug: "social-impact",
    category: "social",
    name: "Social Impact Assessment",
    short: "Measure community and social performance with a theory of change.",
    what:
      "Social impact assessment structures how your organisation understands and measures its effect on communities and stakeholders — from theory of change to outcome indicators.",
    why:
      "CSR budgets, community programmes and social commitments are often measured in money spent rather than outcomes achieved. Impact measurement turns activity into evidence and improves decisions.",
    who: ["CSR and sustainability teams", "Foundations and development organisations", "Infrastructure and project developers", "Leadership reporting social performance"],
    do: [
      "Define community impact objectives and theory of change",
      "Design outcome indicators and measurement approach",
      "Assess social performance against objectives",
      "Recommend improvements to programme design",
    ],
    deliverables: ["Theory of change", "Impact measurement framework", "Outcome indicators", "Social performance report"],
    frameworks: ["GRI", "UN SDGs", "BRSR"],
    process: ["Objectives and stakeholders", "Theory of change workshop", "Indicator design", "Measurement and reporting"],
    next: "Measure What Matters",
  }),
  svc({
    slug: "esg-training",
    category: "social",
    name: "ESG / Sustainability Training",
    short: "Capability building from board briefings to hands-on workshops.",
    what:
      "Training programmes that build genuine ESG capability: board and leadership briefings, employee awareness, ESG fundamentals, BRSR training, climate training, sustainable finance and responsible investment, plus custom workshops.",
    why:
      "ESG programmes stall when understanding is uneven across an organisation. Training turns strategy documents into shared language, better questions and better execution.",
    who: ["Boards and senior leadership", "Sustainability and ESG teams", "Finance, HR and risk functions", "Whole-organisation awareness programmes"],
    do: [
      "Design and deliver board and leadership training",
      "Run employee awareness and ESG fundamentals sessions",
      "Deliver BRSR, climate, sustainable finance and responsible investment training",
      "Build custom workshops around your sector and data",
    ],
    deliverables: ["Training needs review", "Custom curriculum", "Workshops and leadership sessions", "ESG toolkit and reference material"],
    frameworks: ["BRSR", "GHG Protocol", "TCFD", "GRI"],
    process: ["Needs assessment", "Curriculum design", "Delivery", "Evaluation and follow-up"],
    next: "Design Corporate ESG Training",
  }),

  // ---------------- GOVERNANCE ----------------
  svc({
    slug: "esg-policy",
    category: "governance",
    name: "ESG Policy Development",
    short: "A complete, coherent policy architecture — not template paperwork.",
    what:
      "We develop the policy suite your organisation needs: ESG policy, sustainability policy, climate policy, biodiversity policy, human rights policy, responsible investment policy, supplier code of conduct, D&I policy and anti-bribery frameworks.",
    why:
      "Policies are the written foundation of governance — for boards, employees, suppliers, raters and investors. Coherent, tailored policies demonstrate intent and set the standards your processes must meet.",
    who: ["Boards and company secretaries", "Compliance and legal teams", "Procurement and supplier management", "Organisations closing policy gaps"],
    do: [
      "Map existing policies and identify gaps",
      "Draft policies aligned to your sector and commitments",
      "Ensure consistency across the policy architecture",
      "Support approval, communication and embedding",
    ],
    deliverables: ["Policy gap assessment", "ESG & sustainability policies", "Supplier code of conduct", "Anti-bribery & anti-corruption framework"],
    frameworks: ["GRI", "BRSR", "UN PRI (investment policies)"],
    process: ["Policy inventory and gap assessment", "Drafting and calibration", "Management and board review", "Rollout guidance"],
    next: "Build Your ESG Policy Framework",
  }),
  svc({
    slug: "esg-governance",
    category: "governance",
    name: "ESG Governance",
    short: "Clear oversight, committees, roles and KPI governance.",
    what:
      "ESG governance design establishes how sustainability decisions get made: board oversight, ESG committees, roles and responsibilities, decision-making rights and KPI governance.",
    why:
      "ESG fails in the gaps between functions. Clear governance means someone owns each commitment, data flows to the right forum, and progress is reviewed with the same discipline as financial performance.",
    who: ["Boards and board committees", "CEOs and executive committees", "Company secretaries", "Organisations formalising ESG oversight"],
    do: [
      "Design ESG governance structure and committee mandates",
      "Define board oversight and escalation paths",
      "Assign roles, responsibilities and decision rights",
      "Establish KPI governance and reporting rhythms",
    ],
    deliverables: ["ESG governance structure", "Committee charters", "Roles & responsibilities map", "KPI governance framework"],
    frameworks: ["BRSR", "GRI", "TCFD (governance pillar)"],
    process: ["Current-state review", "Structure and mandate design", "Approval and adoption", "Operating rhythm setup"],
    next: "Strengthen ESG Oversight",
  }),
  svc({
    slug: "esg-risk",
    category: "governance",
    name: "ESG Risk Assessment",
    short: "Identify, prioritise and mitigate ESG risks within your risk framework.",
    what:
      "A structured assessment of ESG risks — environmental, social and governance — integrated into your existing risk management approach: identification, prioritisation, materiality, controls and mitigation roadmap.",
    why:
      "ESG risks — from climate exposure to supply-chain practices — carry financial, regulatory and reputational consequences. Integrating them into enterprise risk management makes them visible and manageable.",
    who: ["Chief risk officers", "Audit and risk committees", "Strategy teams", "Financial institutions"],
    do: [
      "Facilitate ESG risk identification",
      "Prioritise risks against likelihood and impact",
      "Connect findings to materiality",
      "Design controls and a mitigation roadmap",
    ],
    deliverables: ["ESG risk register", "Risk prioritisation", "Materiality linkage", "Controls & mitigation roadmap"],
    frameworks: ["TCFD", "BRSR", "GRI"],
    process: ["Risk identification workshops", "Prioritisation", "Control assessment", "Mitigation planning"],
    next: "Map Your ESG Risks",
  }),

  // ---------------- REPORTING ----------------
  svc({
    slug: "brsr",
    category: "reporting",
    name: "BRSR & BRSR Core Readiness",
    short: "From data gaps to a defensible BRSR submission.",
    what:
      "We support the full BRSR journey: readiness assessment, data collection, gap assessment, BRSR preparation, evidence management and BRSR Core readiness.",
    why:
      "BRSR requirements ask listed entities for structured, comparable ESG data — and BRSR Core raises expectations around assurance readiness. Organisations that build data controls early report with far less strain.",
    who: ["Listed companies", "Entities approaching listing thresholds", "Subsidiaries of listed groups", "Teams owning BRSR preparation"],
    do: [
      "Run a BRSR readiness and gap assessment",
      "Design data collection across the disclosure indicators",
      "Prepare and review BRSR disclosures",
      "Organise evidence documentation for BRSR Core readiness",
      "Facilitate management review before submission",
    ],
    deliverables: ["BRSR readiness assessment", "Data collection framework", "Gap assessment", "BRSR drafting support", "Evidence documentation"],
    frameworks: ["BRSR", "BRSR Core", "GRI (alignment)"],
    process: ["Readiness assessment", "Data collection", "Gap assessment & remediation", "Drafting and management review"],
    next: "Discuss Your BRSR Requirement",
  }),
  svc({
    slug: "sustainability-reporting",
    category: "reporting",
    name: "Sustainability Reporting",
    short: "GRI, SASB, ISSB, IFRS S1/S2 and TCFD-aligned reports.",
    what:
      "End-to-end sustainability reporting support: framework selection, content development, data consolidation and report preparation aligned with GRI, SASB, ISSB (IFRS S1, IFRS S2) and TCFD-aligned climate disclosures.",
    why:
      "A sustainability report is a public statement of what your organisation measures and manages. Done well, it builds credibility with investors, customers and employees; done hastily, it invites questions you cannot answer.",
    who: ["Listed and large private companies", "Organisations moving from basic to standards-aligned reporting", "Groups reporting across jurisdictions"],
    do: [
      "Advise on framework selection and scope",
      "Consolidate ESG data and narrative",
      "Prepare standards-aligned sustainability reports",
      "Support TCFD-aligned climate disclosure sections",
    ],
    deliverables: ["Reporting framework assessment", "Sustainability report", "GRI / SASB / ISSB-aligned content", "TCFD-aligned climate disclosures"],
    frameworks: ["GRI", "SASB", "ISSB", "IFRS S1", "IFRS S2", "TCFD"],
    process: ["Framework and scope", "Data and narrative gathering", "Drafting", "Review and publication support"],
    next: "Plan Your Sustainability Report",
  }),
  svc({
    slug: "esg-investor-disclosure",
    category: "reporting",
    name: "ESG Investor Disclosure",
    short: "Questionnaires, data rooms, ratings and due diligence support.",
    what:
      "We help organisations respond to investor ESG requirements: questionnaires, ESG data rooms, ESG rating support, investor reporting and ESG due diligence preparation.",
    why:
      "Investors, lenders and rating providers each ask for ESG information differently. Preparing once — with organised data and evidence — reduces friction across every subsequent request.",
    who: ["Companies raising capital", "Borrowers facing lender ESG questionnaires", "Companies engaging with ESG ratings", "IR and finance teams"],
    do: [
      "Prepare investor ESG questionnaire responses",
      "Assemble ESG data rooms",
      "Support ESG rating engagements",
      "Prepare for investor ESG due diligence",
    ],
    deliverables: ["Investor questionnaire responses", "ESG data room structure", "Rating support pack", "Due diligence readiness review"],
    frameworks: ["ISSB", "SASB", "GRI"],
    process: ["Requirements mapping", "Data assembly", "Response preparation", "Diligence support"],
    next: "Prepare for Investor Questions",
  }),

  // ---------------- RESPONSIBLE INVESTMENT ----------------
  svc({
    slug: "responsible-investment",
    category: "responsible-investment",
    name: "Responsible Investment Advisory",
    short: "ESG across the investment process — integration, diligence, monitoring.",
    what:
      "A dedicated responsible investment practice covering ESG integration, ESG due diligence, ESG screening, Responsible Investment Policy, portfolio ESG assessment, PAI assessment, SFDR support, DNSH assessment, sustainable investment assessment, portfolio carbon assessment, climate risk assessment and ESG monitoring frameworks.",
    why:
      "Capital is increasingly accountable for the ESG consequences of its decisions. Whether the driver is regulation, LP expectations or genuine conviction, responsible investment needs structure: policy, process, data and monitoring.",
    who: ["Investors and fund managers", "Financial institutions", "Family offices and asset owners", "Funds engaging with sustainable finance frameworks"],
    do: [
      "Design ESG integration across the investment process",
      "Run ESG due diligence on targets and portfolios",
      "Draft Responsible Investment Policy and screening approach",
      "Support PAI, SFDR and DNSH-related assessment work",
      "Build portfolio ESG, carbon and climate monitoring",
    ],
    deliverables: ["ESG integration framework", "ESG due diligence reports", "Responsible Investment Policy", "Portfolio ESG & carbon assessment", "PAI / SFDR / DNSH support", "ESG monitoring framework"],
    frameworks: ["UN PRI", "SFDR", "EU Taxonomy", "IFRS S2", "GHG Protocol"],
    process: ["Investment process review", "Policy and screening design", "Integration and diligence", "Monitoring and reporting"],
    next: "Discuss Your Investment ESG Framework",
  }),
];

export const serviceBySlug = (slug) => services.find((s) => s.slug === slug);
export const servicesByCategory = (catId) => services.filter((s) => s.category === catId);
